// ** Modules
import { BlocksRenderer, type BlocksContent } from '@strapi/blocks-react-renderer';

// ** UI
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';

// ** Styles
import { DIN } from '@/styles/globalStyles';

// ** Types
import type { ComponentProps } from 'react';
import type { TypographyProps } from '@mui/material/Typography';

type BlocksRendererProps = ComponentProps<typeof BlocksRenderer>;
type BlocksConfig = BlocksRendererProps['blocks'];

export type BlockType = 'paragraph' | 'heading' | 'list' | 'listItem' | 'quote' | 'code' | 'link';

export type RendererOptions = {
	variantBody?: 'Normal' | 'Primary';
	variantHeading1?: 'Normal' | 'Primary';
	variantHeading2?: 'Normal' | 'Primary';
	variantHeading3?: 'Normal' | 'Primary';
	variantHeading4?: 'Normal' | 'Primary';
	variantHeading5?: 'Normal' | 'Primary';
	variantHeading6?: 'Normal' | 'Primary';
	overrides?: Partial<Record<BlockType, SxProps<Theme>>>;
};

type BlocksContentRendererProps = {
	content: BlocksContent;
	options?: RendererOptions;
};

const variantMapping: Record<string, string> = {
	Normal: 'normal',
	Primary: 'primary'
};

const mapVariant = (type: string, variant?: string): TypographyProps['variant'] => {
	return `${type}-${variantMapping[variant || 'Normal'] || 'normal'}` as TypographyProps['variant'];
};

const applyFirstChildStyles = (isFirst: boolean) => {
	return isFirst ? { mt: 0 } : { mt: 3 };
};

const BlocksContentRenderer = ({ content, options = {} }: BlocksContentRendererProps) => {
	const theme = useTheme();

	// Kept exactly intact as your core layout requirements dictated
	const {
		variantBody,
		variantHeading1,
		variantHeading2,
		variantHeading3,
		variantHeading4,
		variantHeading5,
		variantHeading6,
		overrides = {}
	} = options;
	const textColor = 'text.primary';

	let isFirstChild = true;
	const getStyles = (type: BlockType, baseStyles: SxProps<Theme> = []): SxProps<Theme> => {
		const override = overrides[type];

		return [
			...(Array.isArray(baseStyles) ? baseStyles : [baseStyles]),
			...(Array.isArray(override) ? override : override ? [override] : [])
		];
	};

	const blocksConfig: BlocksConfig = {
		paragraph: ({ children }) => {
			const styles = applyFirstChildStyles(isFirstChild);
			isFirstChild = false;

			const hasCssOverrideColor =
				overrides.paragraph && typeof overrides.paragraph === 'object' && 'color' in overrides.paragraph;
			const hasVariantOverride = variantBody !== undefined && variantBody !== 'Normal';
			const shouldApplyDefaultColor = !hasCssOverrideColor && !hasVariantOverride;

			return (
				<Typography
					variant={mapVariant('body', variantBody)}
					component="p"
					sx={getStyles('paragraph', {
						...(shouldApplyDefaultColor ? { color: textColor } : {}),
						...styles
					})}
				>
					{children}
				</Typography>
			);
		},
		heading: ({ children, level }) => {
			const styles = applyFirstChildStyles(isFirstChild);
			isFirstChild = false;

			const headingVariants: Record<number, 'Normal' | 'Primary' | undefined> = {
				1: variantHeading1,
				2: variantHeading2,
				3: variantHeading3,
				4: variantHeading4,
				5: variantHeading5,
				6: variantHeading6
			};

			const variantSetting = headingVariants[level];
			const currentVariant = mapVariant(`h${level}`, variantSetting);

			const hasCssOverrideColor =
				overrides.heading && typeof overrides.heading === 'object' && 'color' in overrides.heading;
			const hasVariantOverride = variantSetting !== undefined && variantSetting !== 'Normal';
			const shouldApplyDefaultColor = !hasCssOverrideColor && !hasVariantOverride;

			return (
				<Typography
					variant={currentVariant}
					component={`h${level}` as any}
					sx={getStyles('heading', {
						...(shouldApplyDefaultColor ? { color: textColor } : {}),
						...styles
					})}
				>
					{children}
				</Typography>
			);
		},
		quote: ({ children }) => (
			<Paper
				elevation={1}
				sx={getStyles('quote', {
					backgroundColor: 'grey.100',
					p: 2,
					my: 2,
					borderLeft: 4,
					borderColor: 'primary.main',
					width: 'max-content'
				})}
			>
				<Typography
					variant="body-md"
					component="blockquote"
					sx={{ margin: 0, fontStyle: 'italic', color: 'text.primary' }}
				>
					{children}
				</Typography>
			</Paper>
		),
		code: ({ plainText }) => (
			<Box
				sx={getStyles('code', {
					width: 'max-content',
					backgroundColor: 'grey.200',
					p: 2,
					borderRadius: 1,
					overflowX: 'auto',
					fontFamily: DIN,
					fontSize: '0.875rem',
					my: 2
				})}
			>
				<pre style={{ margin: 0 }}>
					<code>{plainText}</code>
				</pre>
			</Box>
		),
		link: ({ children, url, ...restProps }) => {
			const linkColor = theme.palette.primary.main;
			const extraProps = restProps as Record<string, any>;
			const target = extraProps.target;
			const rel = extraProps.rel;

			return (
				<Link href={url} target={target} rel={rel} style={{ color: linkColor, textDecoration: 'underline' }}>
					{children}
				</Link>
			);
		},
		list: ({ format, children }) => {
			const isOrdered = format === 'ordered';
			return (
				<List
					sx={getStyles('list', {
						listStyleType: isOrdered ? 'decimal' : 'disc',
						pl: 4,
						color: textColor,
						'& .MuiListItem-root': { display: 'list-item' }
					})}
					component={isOrdered ? 'ol' : 'ul'}
				>
					{children}
				</List>
			);
		},
		'list-item': ({ children }) => (
			<ListItem sx={{ display: 'list-item', pl: 1.5 }}>
				<Typography variant={mapVariant('body', variantBody)} sx={getStyles('listItem', { color: textColor })}>
					{children}
				</Typography>
			</ListItem>
		)
	};

	return <BlocksRenderer content={content} blocks={blocksConfig} />;
};

export default BlocksContentRenderer;
